import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

export type AppRole = "admin" | "moderator" | "user";

/**
 * Shared role hook backed by `public.user_roles`.
 *
 * Read-only: this hook never inserts, updates, or deletes roles. All role
 * writes must happen server-side (SECURITY DEFINER RPC or service-role code).
 * All privileged actions remain enforced by `has_role()` inside the RPCs;
 * this hook only drives UI presentation and the /admin route guard.
 */
export function useRole() {
  const { user, loading: authLoading } = useAuth();

  const q = useQuery({
    queryKey: ["user-roles", user?.id],
    enabled: !!user,
    staleTime: 60_000,
    queryFn: async (): Promise<AppRole[]> => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user!.id);
      if (error) throw error;
      return (data ?? []).map((r) => r.role as AppRole);
    },
  });

  const roles = q.data ?? [];
  const isAdmin = roles.includes("admin");
  const isModerator = roles.includes("moderator");
  const isAdminOrModerator = isAdmin || isModerator;
  const role: AppRole = isAdmin ? "admin" : isModerator ? "moderator" : "user";

  return {
    role,
    isAdmin,
    isModerator,
    isAdminOrModerator,
    isLoading: authLoading || (!!user && q.isLoading),
    refetch: q.refetch,
  };
}
